<template>
  <div class="documents-upload mb-2">
    <div class="mb-2">
      <label class="form-label mb-1">{{
        $t("views.uploadDocuments.actions.selectTargetFolder")
      }}</label>
      <TreeSelect
        v-model="selectedFolderId"
        :options="treeSelectOptions"
        :placeholder="
          $t('views.uploadDocuments.actions.selectFolderPlaceholder')
        "
        selection-mode="single"
        class="w-full"
        :loading="foldersLoading"
        size="small"
      />
    </div>

    <div class="row gap-4 flex-1">
      <div>
        <div
          class="upload-box border border-1 rounded border-gray p-4 cursor-pointer"
          @dragenter.prevent
          @dragover.prevent
          @drop.prevent="handleFileDrop"
        >
          <div
            class="d-flex flex-column align-items-center justify-content-center"
          >
            <i
              class="pi pi-cloud-upload p-3 fs-1 text-muted mb-2 opacity-50"
            ></i>

            <p class="mb-1">
              {{ $t("views.uploadDocuments.actions.dragAndDrop") }}
            </p>

            <p class="text-muted small mt-1 mb-1">
              {{ $t("views.uploadDocuments.actions.or") }}
            </p>
            <div>
              <i
                class="pi pi-file border border-1 rounded-circle p-2 fs-4 me-2"
                @click="$refs.fileInput.click()"
              ></i>
              <i
                class="pi pi-folder-open border border-1 rounded-circle p-2 fs-4"
                @click="$refs.folderInput.click()"
              ></i>
            </div>

            <input
              ref="fileInput"
              type="file"
              multiple
              class="d-none"
              :accept="acceptedFileTypes"
              @change="handleUpload"
            />
            <input
              ref="folderInput"
              type="file"
              webkitdirectory
              directory
              class="d-none"
              :accept="acceptedFileTypes"
              @change="handleUpload"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from "vue"
import { useUploadStore } from "@/store"
import { useI18n } from "vue-i18n"

const { t } = useI18n()
const uploadStore = useUploadStore()
const acceptedFileTypes = ref("image/*,.pdf,.doc,.docx,.xls,.xlsx")
const selectedFolderId = ref(null)
const folderData = ref({})
const foldersLoading = ref(false)

const treeSelectOptions = computed(() => {
  if (
    !folderData.value ||
    (!folderData.value.children && !folderData.value.id)
  ) {
    return [
      {
        key: "main",
        label: t("views.uploadDocuments.folders.mainFolder"),
        icon: "pi pi-home"
      }
    ]
  }

  const options = [
    {
      key: "main",
      label: t("views.uploadDocuments.folders.mainFolder"),
      icon: "pi pi-home"
    }
  ]

  if (folderData.value.children && folderData.value.children.length > 0) {
    const tree = buildTreeSelectStructure(folderData.value.children)
    options.push(...tree)
  }

  return options
})

const buildTreeSelectStructure = (folders) => {
  return folders.map((folder) => {
    const node = {
      key: folder.id,
      label: folder.name,
      icon: "pi pi-folder"
    }

    if (folder.children && folder.children.length > 0) {
      node.children = buildTreeSelectStructure(folder.children)
    }

    return node
  })
}

const loadFolders = async () => {
  try {
    foldersLoading.value = true
    folderData.value = await uploadStore.loadFoldersList()
  } finally {
    foldersLoading.value = false
  }
}

watch(selectedFolderId, (newFolderId) => {
  let targetId = null

  if (newFolderId) {
    if (typeof newFolderId === "object") {
      const keys = Object.keys(newFolderId)
      const folderKey = keys.length > 0 ? keys[0] : null
      targetId = folderKey === "main" || !folderKey ? null : folderKey
    } else {
      targetId = newFolderId === "main" ? null : newFolderId
    }
  }

  uploadStore.setTargetFolderId(targetId)
})

const handleFileDrop = (event) => {
  event.preventDefault()
  const items = event.dataTransfer.items
  const files = []

  const processEntry = async (entry) => {
    if (entry.isFile) {
      const file = await new Promise((resolve) => entry.file(resolve))
      files.push(file)
    } else if (entry.isDirectory) {
      const reader = entry.createReader()
      const entries = await new Promise((resolve) =>
        reader.readEntries(resolve)
      )
      await Promise.all(entries.map(processEntry))
    }
  }

  Promise.all(
    Array.from(items)
      .map((item) => item.webkitGetAsEntry())
      .filter((entry) => entry != null)
      .map(processEntry)
  ).then(() => {
    uploadStore.addDocuments(files, acceptedFileTypes.value)
  })
}

const handleUpload = (event) => {
  try {
    const files = event.files || event.target.files
    uploadStore.addDocuments(files, acceptedFileTypes.value)
  } finally {
    if (event.target) {
      event.target.value = ""
    }
  }
}

onMounted(async () => {
  await loadFolders()

  await nextTick()

  if (treeSelectOptions.value.length > 0) {
    const firstOption = treeSelectOptions.value[0]
    selectedFolderId.value = { [firstOption.key]: true }
  }

  uploadStore.setTargetFolderId(null)
})
</script>
